import React, { useState, useEffect } from "react";
import { 
  LayoutDashboard, 
  Wine, 
  ClipboardList, 
  MessageSquare, 
  Plus, 
  Trash2, 
  Edit3, 
  RefreshCw, 
  Calendar, 
  User, 
  MapPin, 
  Package, 
  DollarSign, 
  Check, 
  Loader2,
  Settings,
  Eye,
  ExternalLink
} from "lucide-react";
import "./App.css";

const API_BASE_URL = "https://ratelistapi.rmproject.in";
// const API_BASE_URL = "http://localhost:5001";

function App() {
  // Navigation State
  const [activeTab, setActiveTab] = useState("dashboard");

  // Catalog & Logs State
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [whatsappLogs, setWhatsappLogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shopDetails, setShopDetails] = useState({
    shopName: "",
    description: "",
    email: "",
    phone: "",
    whatsapp: "",
    image: "",
    deliveryCharge: 0,
    discount: 0
  });
  const [newCategoryName, setNewCategoryName] = useState("");
  const [shopImageFile, setShopImageFile] = useState(null);
  const [shopFileInputKey, setShopFileInputKey] = useState(Date.now());

  // Loading States
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Order Details / Status Update States
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  // Form State for Products (Create / Edit)
  const [productForm, setProductForm] = useState({
    name: "",
    price: "",
    category: "",
    description: "",
    image: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=500&auto=format&fit=crop&q=60";
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      return imagePath;
    }
    return `${API_BASE_URL}${imagePath}`;
  };

  // Load initial data
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch products
      const prodRes = await fetch(`${API_BASE_URL}/api/products`);
      if (!prodRes.ok) throw new Error("Failed to load products");
      const prodData = await prodRes.json();
      setProducts(prodData);

      // Fetch orders
      const orderRes = await fetch(`${API_BASE_URL}/api/orders`);
      if (!orderRes.ok) throw new Error("Failed to load orders");
      const orderData = await orderRes.json();
      setOrders(orderData);

      // Fetch WhatsApp logs
      const logRes = await fetch(`${API_BASE_URL}/api/whatsapp-logs`);
      if (!logRes.ok) throw new Error("Failed to load WhatsApp logs");
      const logData = await logRes.json();
      setWhatsappLogs(logData);

      // Fetch Categories
      const catRes = await fetch(`${API_BASE_URL}/api/categories`);
      if (catRes.ok) {
        const catData = await catRes.json();
        setCategories(catData);
        if (catData.length > 0) {
          setProductForm(prev => ({
            ...prev,
            category: prev.category || catData[0].name
          }));
        }
      }

      // Fetch Shop Details
      const shopRes = await fetch(`${API_BASE_URL}/api/shop-details`);
      if (shopRes.ok) {
        const shopData = await shopRes.json();
        setShopDetails(shopData);
      }

    } catch (err) {
      console.error(err);
      setError("Unable to connect to the backend server. Please make sure the backend is running.");
    } finally {
      setLoading(false);
    }
  };

  // Form input change handler
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductForm(prev => ({ ...prev, [name]: value }));
  };

  // Submit Product Form (Add or Edit)
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.price) {
      alert("Name and Price are required.");
      return;
    }

    try {
      setSubmitting(true);
      
      const formData = new FormData();
      formData.append("name", productForm.name);
      formData.append("price", productForm.price);
      formData.append("category", productForm.category);
      formData.append("description", productForm.description);
      
      if (imageFile) {
        formData.append("imageFile", imageFile);
      } else {
        formData.append("image", productForm.image);
      }

      const url = editingId 
        ? `${API_BASE_URL}/api/products/${editingId}`
        : `${API_BASE_URL}/api/products`;
      
      const method = editingId ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        body: formData
      });

      if (!response.ok) {
        throw new Error("Failed to save product");
      }

      // Refresh product list
      const prodRes = await fetch(`${API_BASE_URL}/api/products`);
      const prodData = await prodRes.json();
      setProducts(prodData);

      // Reset form
      setProductForm({
        name: "",
        price: "",
        category: categories[0]?.name || "Whisky",
        description: "",
        image: ""
      });
      setImageFile(null);
      setFileInputKey(Date.now());
      setEditingId(null);
      alert(editingId ? "Product updated successfully!" : "Product added successfully!");
    } catch (err) {
      console.error(err);
      alert("Error occurred while saving product.");
    } finally {
      setSubmitting(false);
    }
  };

  // Trigger Edit Mode
  const startEdit = (product) => {
    setEditingId(product._id || product.id);
    setProductForm({
      name: product.name,
      price: product.price.toString(),
      category: product.category,
      description: product.description || "",
      image: product.image || ""
    });
  };

  // Cancel Edit Mode
  const cancelEdit = () => {
    setEditingId(null);
    setProductForm({
      name: "",
      price: "",
      category: categories[0]?.name || "Whisky",
      description: "",
      image: ""
    });
    setImageFile(null);
    setFileInputKey(Date.now());
  };

  // Delete Product
  const handleDeleteProduct = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product from catalog?")) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/products/${id}`, {
        method: "DELETE"
      });

      if (!response.ok) {
        throw new Error("Failed to delete product");
      }

      setProducts(prev => prev.filter(p => (p._id || p.id) !== id));
      alert("Product deleted successfully!");
    } catch (err) {
      console.error(err);
      alert("Error deleting product.");
    }
  };

  // Handle Shop Details Submit
  const handleShopDetailsSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("shopName", shopDetails.shopName);
      formData.append("description", shopDetails.description);
      formData.append("email", shopDetails.email);
      formData.append("phone", shopDetails.phone);
      formData.append("whatsapp", shopDetails.whatsapp);
      formData.append("deliveryCharge", shopDetails.deliveryCharge);
      formData.append("discount", shopDetails.discount);

      if (shopImageFile) {
        formData.append("imageFile", shopImageFile);
      } else {
        formData.append("image", shopDetails.image || "");
      }

      const response = await fetch(`${API_BASE_URL}/api/shop-details`, {
        method: "PUT",
        body: formData
      });

      if (!response.ok) throw new Error("Failed to update shop details");
      const updatedData = await response.json();
      setShopDetails(updatedData);
      setShopImageFile(null);
      setShopFileInputKey(Date.now());
      alert("Shop details updated successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to update shop details.");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Shop details input change
  const handleShopInputChange = (e) => {
    const { name, value } = e.target;
    setShopDetails(prev => ({ ...prev, [name]: value }));
  };

  // Add category
  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategoryName || !newCategoryName.trim()) return;

    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/api/categories`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: newCategoryName.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to add category");
      }

      const newCat = await response.json();
      setCategories(prev => [...prev, newCat].sort((a, b) => a.name.localeCompare(b.name)));
      setNewCategoryName("");
      alert("Category added successfully!");
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to add category.");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete category
  const handleDeleteCategory = async (catId, catName) => {
    if (!window.confirm(`Are you sure you want to delete category "${catName}"?`)) {
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch(`${API_BASE_URL}/api/categories/${catId}`, {
        method: "DELETE"
      });

      if (!response.ok) throw new Error("Failed to delete category");

      setCategories(prev => prev.filter(c => (c._id || c.id) !== catId));
      alert("Category deleted successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to delete category.");
    } finally {
      setSubmitting(false);
    }
  };

  // Update order status
  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      setUpdatingOrderId(orderId);
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error("Failed to update order status");
      }

      const updatedOrder = await response.json();

      // Update local orders state
      setOrders(prev => prev.map(o => (o.id === orderId || o._id === orderId ? updatedOrder : o)));

      // Update selectedOrder state if open
      setSelectedOrder(prev => {
        if (prev && (prev.id === orderId || prev._id === orderId)) {
          return updatedOrder;
        }
        return prev;
      });

    } catch (err) {
      console.error(err);
      alert("Failed to update order status.");
    } finally {
      setUpdatingOrderId(null);
    }
  };

  // Calculate stats for dashboard
  const totalRevenue = orders.reduce((sum, o) => sum + o.totalAmount, 0);
  const totalItemsSold = orders.reduce((sum, o) => sum + o.items.reduce((s, i) => s + i.quantity, 0), 0);

  return (
    <div className="admin-layout">
      {/* Sidebar Navigation */}
      <aside className="admin-sidebar">
        <div className="sidebar-brand">
          <div className="brand-icon">🍷</div>
          <div className="brand-text">WineAdmin</div>
        </div>

        <ul className="sidebar-menu">
          <li>
            <button 
              className={`menu-item-btn ${activeTab === "dashboard" ? "active" : ""}`}
              onClick={() => setActiveTab("dashboard")}
            >
              <LayoutDashboard />
              <span>Dashboard</span>
            </button>
          </li>
          <li>
            <button 
              className={`menu-item-btn ${activeTab === "products" ? "active" : ""}`}
              onClick={() => setActiveTab("products")}
            >
              <Wine />
              <span>Products Catalog</span>
            </button>
          </li>
          <li>
            <button 
              className={`menu-item-btn ${activeTab === "orders" ? "active" : ""}`}
              onClick={() => setActiveTab("orders")}
            >
              <ClipboardList />
              <span>Orders Log</span>
            </button>
          </li>
          <li>
            <button 
              className={`menu-item-btn ${activeTab === "whatsapp" ? "active" : ""}`}
              onClick={() => setActiveTab("whatsapp")}
            >
              <MessageSquare />
              <span>WhatsApp Simulator</span>
            </button>
          </li>
          <li>
            <button 
              className={`menu-item-btn ${activeTab === "settings" ? "active" : ""}`}
              onClick={() => setActiveTab("settings")}
            >
              <Settings />
              <span>Shop Settings</span>
            </button>
          </li>
        </ul>

        <div className="sidebar-footer">
          <p>© 2026 Wine Shop Admin</p>
          <p style={{ marginTop: "4px", fontSize: "0.75rem", opacity: 0.7 }}>v1.0.0 (Beta)</p>
        </div>
      </aside>

      {/* Main Panel */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <h2 className="admin-header-title">
            {activeTab === "dashboard" && "Dashboard Overview"}
            {activeTab === "products" && "Products Catalog Management"}
            {activeTab === "orders" && "Customer Orders Log"}
            {activeTab === "whatsapp" && "WhatsApp Notification Simulator Logs"}
            {activeTab === "settings" && "Shop Settings & Master Table"}
          </h2>
          
          <div className="admin-header-profile">
            <button className="btn btn-secondary" onClick={fetchAllData} title="Refresh Data">
              <RefreshCw size={16} />
              <span>Refresh</span>
            </button>
            <div className="profile-avatar">A</div>
            <span style={{ fontWeight: "600", fontSize: "0.9rem" }}>Admin Owner</span>
          </div>
        </header>

        {/* Content Area */}
        <div className="admin-content">
          {error && (
            <div style={{
              background: "#fee2e2",
              border: "1px solid #fca5a5",
              color: "#b91c1c",
              padding: "16px",
              borderRadius: "12px",
              marginBottom: "24px",
              fontWeight: "600"
            }}>
              {error}
            </div>
          )}

          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "300px", gap: "12px" }}>
              <Loader2 className="animate-spin" style={{ width: "40px", height: "40px", color: "var(--admin-primary)" }} />
              <span style={{ fontWeight: "600", color: "var(--admin-text-muted)" }}>Loading records...</span>
            </div>
          ) : (
            <>
              {/* --- DASHBOARD TAB --- */}
              {activeTab === "dashboard" && (
                <div>
                  {/* Stats Grid */}
                  <div className="overview-grid">
                    <div className="stat-card">
                      <div className="stat-info">
                        <span className="stat-label">Total Products</span>
                        <h3 className="stat-value">{products.length}</h3>
                      </div>
                      <div className="stat-icon blue">
                        <Wine size={24} />
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-info">
                        <span className="stat-label">Total Orders</span>
                        <h3 className="stat-value">{orders.length}</h3>
                      </div>
                      <div className="stat-icon orange">
                        <ClipboardList size={24} />
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-info">
                        <span className="stat-label">Total Revenue</span>
                        <h3 className="stat-value">₹{totalRevenue.toFixed(2)}</h3>
                      </div>
                      <div className="stat-icon green">
                        <DollarSign size={24} />
                      </div>
                    </div>

                    <div className="stat-card">
                      <div className="stat-info">
                        <span className="stat-label">Items Distributed</span>
                        <h3 className="stat-value">{totalItemsSold}</h3>
                      </div>
                      <div className="stat-icon blue">
                        <Package size={24} />
                      </div>
                    </div>
                  </div>

                  {/* Split Layout: Recent Orders + WhatsApp Simulation Logs */}
                  <div className="products-layout" style={{ gridTemplateColumns: "1.2fr 1fr" }}>
                    {/* Recent Orders Card */}
                    <div className="admin-card">
                      <div className="card-header">
                        <h3 className="card-title">Recent Customer Orders</h3>
                      </div>
                      <div className="card-body" style={{ padding: "0" }}>
                        {orders.length === 0 ? (
                          <p style={{ padding: "24px", color: "var(--admin-text-muted)" }}>No orders placed yet.</p>
                        ) : (
                          <div className="table-responsive">
                            <table className="admin-table">
                              <thead>
                                <tr>
                                  <th>Order ID</th>
                                  <th>Customer</th>
                                  <th>Total</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {orders.slice(-5).reverse().map((order) => (
                                  <tr key={order.id}>
                                    <td style={{ fontWeight: "700" }}>{order.id}</td>
                                    <td>{order.customerName}</td>
                                    <td style={{ fontWeight: "600" }}>₹{order.totalAmount}</td>
                                    <td>
                                      <span className={`badge ${order.status}`}>{order.status}</span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Simulation logs on Dashboard */}
                    <div className="admin-card">
                      <div className="card-header">
                        <h3 className="card-title">Recent WhatsApp Notifications</h3>
                      </div>
                      <div className="card-body">
                        {whatsappLogs.length === 0 ? (
                          <p style={{ color: "var(--admin-text-muted)" }}>No simulated messages logged.</p>
                        ) : (
                          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            {whatsappLogs.slice(-3).reverse().map((log, index) => (
                              <div key={index} style={{ padding: "12px", border: "1px solid var(--admin-border)", borderRadius: "8px", background: "#f8fafc", fontSize: "0.85rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px", fontWeight: "600" }}>
                                  <span>To: {log.to}</span>
                                  <span style={{ 
                                    fontSize: "0.7rem", 
                                    padding: "1px 5px", 
                                    borderRadius: "4px", 
                                    background: log.role === "admin" ? "#e0e7ff" : "#d1fae5",
                                    color: log.role === "admin" ? "#4338ca" : "#065f46"
                                  }}>{log.role.toUpperCase()}</span>
                                </div>
                                <div style={{ fontFamily: "monospace", fontSize: "0.75rem", whiteSpace: "pre-wrap", background: "#fff", padding: "8px", border: "1px solid #e2e8f0" }}>
                                  {log.message.substring(0, 100)}...
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* --- PRODUCTS TAB --- */}
              {activeTab === "products" && (
                <div className="products-layout">
                  {/* Left: Add / Edit Product Form */}
                  <div className="admin-card">
                    <div className="card-header">
                      <h3 className="card-title">
                        {editingId ? "Edit Catalog Item" : "Create New Catalog Item"}
                      </h3>
                      {editingId && (
                        <button className="btn btn-secondary" style={{ padding: "4px 8px", fontSize: "0.75rem" }} onClick={cancelEdit}>
                          Cancel Edit
                        </button>
                      )}
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleProductSubmit} className="admin-form">
                        <div className="form-group">
                          <label htmlFor="name">Item Name *</label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            className="form-input"
                            placeholder="e.g. 100 Piper 8 years 750 ml"
                            value={productForm.name}
                            onChange={handleInputChange}
                            required
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="price">Price (₹) *</label>
                            <input
                              type="number"
                              id="price"
                              name="price"
                              className="form-input"
                              placeholder="e.g. 2180"
                              value={productForm.price}
                              onChange={handleInputChange}
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="category">Category *</label>
                            <select
                              id="category"
                              name="category"
                              className="form-input"
                              value={productForm.category}
                              onChange={handleInputChange}
                              required
                            >
                              {categories.map((cat) => (
                                <option key={cat._id || cat.id} value={cat.name}>
                                  {cat.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="imageFile">Upload Image File (Will be compressed)</label>
                          <input
                            key={fileInputKey}
                            type="file"
                            id="imageFile"
                            name="imageFile"
                            accept="image/*"
                            className="form-input"
                            onChange={(e) => setImageFile(e.target.files[0])}
                          />
                        </div>

                        <div className="form-group" style={{ textAlign: "center", margin: "4px 0", color: "var(--admin-text-muted)", fontSize: "0.8rem", fontWeight: "600" }}>
                          <span>— OR —</span>
                        </div>

                        <div className="form-group">
                          <label htmlFor="image">Image URL (Fallback)</label>
                          <input
                            type="text"
                            id="image"
                            name="image"
                            className="form-input"
                            placeholder="https://images.unsplash.com/..."
                            value={productForm.image}
                            onChange={handleInputChange}
                          />
                          <span style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)" }}>
                            Leave blank to use default whiskey bottle image placeholder.
                          </span>
                        </div>

                        <div className="form-group">
                          <label htmlFor="description">Description</label>
                          <textarea
                            id="description"
                            name="description"
                            className="form-input"
                            rows="3"
                            placeholder="A brief explanation of taste, age, origin..."
                            value={productForm.description}
                            onChange={handleInputChange}
                          />
                        </div>

                        <div className="form-actions">
                          <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={submitting}>
                            {submitting ? (
                              <Loader2 className="animate-spin" size={16} />
                            ) : editingId ? (
                              <span>Update Item</span>
                            ) : (
                              <>
                                <Plus size={16} />
                                <span>Create Item</span>
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Right: Products List Table */}
                  <div className="admin-card">
                    <div className="card-header">
                      <h3 className="card-title">Catalog Inventory ({products.length} items)</h3>
                    </div>
                    <div className="card-body" style={{ padding: "0" }}>
                      {products.length === 0 ? (
                        <p style={{ padding: "24px", color: "var(--admin-text-muted)" }}>No products in inventory.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Product Details</th>
                                <th>Category</th>
                                <th>Price</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {products.map((product) => (
                                <tr key={product._id || product.id}>
                                  <td>
                                    <div className="product-row-info">
                                      <img
                                        src={getImageUrl(product.image)}
                                        alt={product.name}
                                        className="table-product-img"
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=500&auto=format&fit=crop&q=60";
                                        }}
                                      />
                                      <div>
                                        <div style={{ fontWeight: "600", fontSize: "0.9rem" }}>{product.name}</div>
                                        <div style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)" }}>
                                          ID: {product._id || product.id}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <span className={`badge ${product.category}`}>{product.category}</span>
                                  </td>
                                  <td style={{ fontWeight: "700" }}>₹{product.price}</td>
                                  <td>
                                    <div className="table-actions">
                                      <button 
                                        className="icon-btn edit" 
                                        onClick={() => startEdit(product)}
                                        title="Edit Product"
                                      >
                                        <Edit3 size={16} />
                                      </button>
                                      <button 
                                        className="icon-btn delete" 
                                        onClick={() => handleDeleteProduct(product._id || product.id)}
                                        title="Delete Product"
                                      >
                                        <Trash2 size={16} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* --- ORDERS TAB --- */}
              {activeTab === "orders" && (
                <div className="admin-card">
                  <div className="card-header">
                    <h3 className="card-title">Placed Orders Logs ({orders.length} entries)</h3>
                  </div>
                  <div className="card-body" style={{ padding: "0" }}>
                    {orders.length === 0 ? (
                      <p style={{ padding: "24px", color: "var(--admin-text-muted)", textAlign: "center" }}>
                        No orders recorded on this server.
                      </p>
                    ) : (
                      <div className="table-responsive">
                        <table className="admin-table">
                          <thead>
                            <tr>
                              <th>Order Info</th>
                              <th>Customer Details</th>
                              <th>Items Summary</th>
                              <th>Total Pay</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orders.slice().reverse().map((order) => (
                              <tr key={order.id}>
                                <td>
                                  <div style={{ fontWeight: "800", color: "var(--admin-primary)" }}>{order.id}</div>
                                  <div style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)", marginTop: "4px" }}>
                                    <Calendar size={12} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
                                    {new Date(order.createdAt).toLocaleString()}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                                    <span style={{ fontWeight: "600" }}>
                                      <User size={12} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
                                      {order.customerName}
                                    </span>
                                    <span style={{ color: "var(--admin-text-muted)" }}>
                                      <MessageSquare size={12} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
                                      {order.customerPhone}
                                    </span>
                                    <span style={{ fontSize: "0.8rem", color: "var(--admin-text-muted)" }}>
                                      <MapPin size={12} style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
                                      {order.customerAddress}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: "flex", flexDirection: "column", gap: "4px", fontSize: "0.85rem" }}>
                                    {order.items.map((item, idx) => (
                                      <div key={idx} style={{ color: "#334155" }}>
                                        • {item.name} <span style={{ fontWeight: "700" }}>x{item.quantity}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td style={{ fontWeight: "800", fontSize: "1rem" }}>
                                  ₹{order.totalAmount.toFixed(2)}
                                </td>
                                <td>
                                  <select
                                    value={order.status}
                                    onChange={(e) => handleUpdateOrderStatus(order.id || order._id, e.target.value)}
                                    className={`status-select ${order.status}`}
                                    disabled={updatingOrderId === (order.id || order._id)}
                                  >
                                    <option value="Pending">Pending</option>
                                    <option value="Processing">Processing</option>
                                    <option value="Delivered">Delivered</option>
                                    <option value="Completed">Completed</option>
                                    <option value="Cancelled">Cancelled</option>
                                  </select>
                                </td>
                                <td>
                                  <button
                                    className="btn btn-secondary"
                                    style={{ padding: "6px 12px", fontSize: "0.8rem", height: "auto" }}
                                    onClick={() => setSelectedOrder(order)}
                                  >
                                    <Eye size={14} />
                                    <span>Details</span>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* --- WHATSAPP SIMULATOR TAB --- */}
              {activeTab === "whatsapp" && (
                <div className="admin-card">
                  <div className="card-header">
                    <h3 className="card-title">Simulated WhatsApp Notifications Log</h3>
                  </div>
                  <div className="card-body">
                    <p style={{ color: "var(--admin-text-muted)", fontSize: "0.9rem", marginBottom: "20px" }}>
                      This logs the WhatsApp messages that are generated by the server. In a live environment, these correspond to Twilio/Meta API payloads.
                    </p>

                    {whatsappLogs.length === 0 ? (
                      <p style={{ color: "var(--admin-text-muted)", textAlign: "center", padding: "24px" }}>
                        No WhatsApp logs available. Place a customer order to test.
                      </p>
                    ) : (
                      <div className="log-list">
                        {whatsappLogs.slice().reverse().map((log, index) => (
                          <div key={index} className="log-card">
                            <div className="log-meta">
                              <span className="log-recipient">
                                📱 Sent to: {log.to}
                                <span className={`log-recipient-badge ${log.role}`}>
                                  {log.role}
                                </span>
                              </span>
                              <span className="log-time">
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                            <div className="log-body">
                              {log.message}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* --- SETTINGS TAB --- */}
              {activeTab === "settings" && (
                <div className="products-layout">
                  {/* Left: Edit Shop Details */}
                  <div className="admin-card">
                    <div className="card-header">
                      <h3 className="card-title">Edit Shop Details</h3>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleShopDetailsSubmit} className="admin-form">
                        <div className="form-group">
                          <label htmlFor="shopName">Shop Name *</label>
                          <input
                            type="text"
                            id="shopName"
                            name="shopName"
                            className="form-input"
                            value={shopDetails.shopName}
                            onChange={handleShopInputChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="description">Shop Subtitle/Description *</label>
                          <textarea
                            id="description"
                            name="description"
                            className="form-input"
                            rows="2"
                            value={shopDetails.description}
                            onChange={handleShopInputChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="email">Email Address *</label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            className="form-input"
                            value={shopDetails.email}
                            onChange={handleShopInputChange}
                            required
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="phone">Display Phone Number (Dialer fallback) *</label>
                          <input
                            type="text"
                            id="phone"
                            name="phone"
                            className="form-input"
                            value={shopDetails.phone}
                            onChange={handleShopInputChange}
                            required
                          />
                        </div>

                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor="deliveryCharge">Delivery Charge (₹) *</label>
                            <input
                              type="number"
                              id="deliveryCharge"
                              name="deliveryCharge"
                              className="form-input"
                              placeholder="e.g. 50 (0 for Free)"
                              value={shopDetails.deliveryCharge}
                              onChange={handleShopInputChange}
                              required
                            />
                          </div>

                          <div className="form-group">
                            <label htmlFor="discount">Flat Discount (₹) *</label>
                            <input
                              type="number"
                              id="discount"
                              name="discount"
                              className="form-input"
                              placeholder="e.g. 100 (0 for None)"
                              value={shopDetails.discount}
                              onChange={handleShopInputChange}
                              required
                            />
                          </div>
                        </div>

                        <div className="form-group">
                          <label htmlFor="whatsapp">WhatsApp Number (e.g. +919602375098) *</label>
                          <input
                            type="text"
                            id="whatsapp"
                            name="whatsapp"
                            className="form-input"
                            value={shopDetails.whatsapp}
                            onChange={handleShopInputChange}
                            required
                          />
                          <span style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)" }}>
                            Must include country code, e.g., +919602375098 (used for automated order alerts and customer messages).
                          </span>
                        </div>

                        <div className="form-group">
                          <label htmlFor="shopImageFile">Upload Shop Image (From PC / Mobile)</label>
                          <input
                            key={shopFileInputKey}
                            type="file"
                            id="shopImageFile"
                            name="shopImageFile"
                            accept="image/*"
                            className="form-input"
                            onChange={(e) => setShopImageFile(e.target.files[0])}
                          />
                        </div>

                        <div className="form-group" style={{ textAlign: "center", margin: "4px 0", color: "var(--admin-text-muted)", fontSize: "0.8rem", fontWeight: "600" }}>
                          <span>— OR —</span>
                        </div>

                        <div className="form-group">
                          <label htmlFor="shopImage">Shop Image / Logo URL</label>
                          <input
                            type="text"
                            id="shopImage"
                            name="image"
                            className="form-input"
                            value={shopDetails.image || ""}
                            placeholder="e.g. https://images.unsplash.com/... or /uploads/..."
                            onChange={handleShopInputChange}
                            required={!shopImageFile}
                          />
                          <span style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)" }}>
                            Displays as the dynamic logo in the header of the customer storefront.
                          </span>
                        </div>

                        <div className="form-actions">
                          <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={submitting}>
                            {submitting ? <Loader2 className="animate-spin" size={16} /> : "Save Settings"}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  {/* Right: Category Master Table Management */}
                  <div className="admin-card">
                    <div className="card-header">
                      <h3 className="card-title">Category Master Table ({categories.length} items)</h3>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleAddCategory} className="admin-form" style={{ marginBottom: "24px" }}>
                        <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
                          <div className="form-group" style={{ flex: 1 }}>
                            <label htmlFor="newCategoryName">Add New Category</label>
                            <input
                              type="text"
                              id="newCategoryName"
                              className="form-input"
                              placeholder="e.g. Gin"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              required
                            />
                          </div>
                          <button type="submit" className="btn btn-primary" style={{ height: "42px" }} disabled={submitting}>
                            <Plus size={16} />
                            <span>Add</span>
                          </button>
                        </div>
                      </form>

                      {categories.length === 0 ? (
                        <p style={{ color: "var(--admin-text-muted)" }}>No categories loaded.</p>
                      ) : (
                        <div className="table-responsive">
                          <table className="admin-table">
                            <thead>
                              <tr>
                                <th>Category Name</th>
                                <th>DB ID</th>
                                <th style={{ textAlign: "right" }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {categories.map((cat) => (
                                <tr key={cat._id || cat.id}>
                                  <td style={{ fontWeight: "600" }}>{cat.name}</td>
                                  <td style={{ fontSize: "0.75rem", color: "var(--admin-text-muted)" }}>{cat._id || cat.id}</td>
                                  <td style={{ textAlign: "right" }}>
                                    <button 
                                      className="icon-btn delete"
                                      onClick={() => handleDeleteCategory(cat._id || cat.id, cat.name)}
                                      title="Delete Category"
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Order Details Modal Overlay */}
      {selectedOrder && (
        <div className="admin-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Order Details: {selectedOrder.id}</h3>
              <button className="close-btn" onClick={() => setSelectedOrder(null)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="order-details-grid">
                {/* Left pane: customer info */}
                <div>
                  <h4 className="details-section-title">Customer Information</h4>
                  
                  <div className="info-row">
                    <span className="info-label">Name:</span>
                    <span className="info-value" style={{ fontWeight: "600" }}>{selectedOrder.customerName}</span>
                  </div>
                  
                  <div className="info-row">
                    <span className="info-label">Phone:</span>
                    <span className="info-value">
                      <a 
                        href={`tel:${selectedOrder.customerPhone}`} 
                        style={{ color: "var(--admin-primary)", textDecoration: "none", fontWeight: "600", display: "inline-flex", alignItems: "center", gap: "4px" }}
                      >
                        {selectedOrder.customerPhone}
                        <ExternalLink size={12} />
                      </a>
                    </span>
                  </div>
                  
                  <div className="info-row">
                    <span className="info-label">Address:</span>
                    <span className="info-value">{selectedOrder.customerAddress}</span>
                  </div>

                  <div className="info-row">
                    <span className="info-label">Placed At:</span>
                    <span className="info-value">{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                  </div>

                  <div className="info-row">
                    <span className="info-label">Status:</span>
                    <span className="info-value">
                      <select
                        value={selectedOrder.status}
                        onChange={(e) => handleUpdateOrderStatus(selectedOrder.id || selectedOrder._id, e.target.value)}
                        className={`status-select ${selectedOrder.status}`}
                        style={{ border: "1px solid var(--admin-border)" }}
                        disabled={updatingOrderId === (selectedOrder.id || selectedOrder._id)}
                      >
                        <option value="Pending">Pending</option>
                        <option value="Processing">Processing</option>
                        <option value="Delivered">Delivered</option>
                        <option value="Completed">Completed</option>
                        <option value="Cancelled">Cancelled</option>
                      </select>
                    </span>
                  </div>
                </div>

                {/* Right pane: Items lists */}
                <div>
                  <h4 className="details-section-title">Items Summary</h4>
                  
                  <table className="modal-items-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Qty</th>
                        <th style={{ textAlign: "right" }}>Price</th>
                        <th style={{ textAlign: "right" }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: "600" }}>{item.name}</td>
                          <td>{item.quantity}</td>
                          <td style={{ textAlign: "right" }}>₹{item.price.toFixed(2)}</td>
                          <td style={{ textAlign: "right", fontWeight: "600" }}>₹{(item.price * item.quantity).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="order-totals">
                    <div className="total-line">
                      <span>Items Subtotal:</span>
                      <span>₹{selectedOrder.items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)}</span>
                    </div>
                    
                    <div className="total-line grand-total">
                      <span>Total Paid (COD):</span>
                      <span>₹{selectedOrder.totalAmount.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <a
                href={`https://api.whatsapp.com/send?phone=${selectedOrder.customerPhone.replace(/\D/g, "")}&text=${encodeURIComponent(
                  `Hello ${selectedOrder.customerName}, this is Wine Shop Admin. Regarding your order ${selectedOrder.id} status: ${selectedOrder.status}.`
                )}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary"
                style={{ background: "#25d366" }}
              >
                <MessageSquare size={16} />
                <span>Contact via WhatsApp</span>
              </a>
              
              <button className="btn btn-secondary" onClick={() => setSelectedOrder(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
